import React, { useEffect, useMemo, useState } from 'react';
import './Reception.css';
import Graph3 from '../components/Graph3';

/* ===================================================
   ★ CONFIGURATION
   =================================================== */
const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// ★ Taille du graphique Graph3 (identique à la version d'origine)
const GRAPH_LARGEUR = '750px';
const GRAPH_HAUTEUR = '400px';

// Colonnes masquées
const MASQUEES_COMMANDES = ['ID', 'Client_id', 'Statut AR'];
const MASQUEES_EMPLACEMENT = ['ID', 'Client_id'];
const COLONNES_STOCK = ['Item', 'Nom Dépôt', 'Emplacement', 'Stock'];

// Libellés corrigés à l'affichage (les données ne changent pas)
const LIBELLES = {
  'Numero de commande': 'Numéro de commande',
  'Numero de ligne': 'N° de ligne',
};

/* ===================================================
   UTILITAIRES
   =================================================== */
const formatDate = (value) => {
  const d = new Date(value);
  if (isNaN(d)) return value;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const estNombre = (v) => v !== null && v !== undefined && v !== '' && !isNaN(Number(String(v).replace(',', '.')));

const toneHorizon = (v) => (v === 'Retard' ? 'danger' : v === 'J' ? 'warn' : 'ok');

const contient = (valeur, filtre) =>
  !filtre || (valeur != null && String(valeur).toLowerCase().includes(filtre.toLowerCase()));

/* ===================================================
   COMPOSANT
   =================================================== */
const Reception = () => {
  useEffect(() => {
    document.title = 'MYSSA – Réception';
  }, []);

  const [commandeFournisseurData, setCommandeFournisseurData] = useState([]);
  const [emplacementVideData, setEmplacementVideData] = useState([]);
  const [stockData, setStockData] = useState([]);
  const [itemFilter, setItemFilter] = useState('');
  const [numeroCommandeFilter, setNumeroCommandeFilter] = useState('');
  const [fournisseurFilter, setFournisseurFilter] = useState('');

  /* ---------- Chargement ---------- */
  useEffect(() => {
    fetch(`${API}/base_de_donnee_cmde_frn`)
      .then(response => response.json())
      .then(data => setCommandeFournisseurData(Array.isArray(data) ? data : []))
      .catch(error => console.error('Erreur base_de_donnee_cmde_frn:', error));

    fetch(`${API}/emplacement_vide`)
      .then(response => response.json())
      .then(data => setEmplacementVideData(Array.isArray(data) ? data : []))
      .catch(error => console.error('Erreur emplacement_vide:', error));

    const fetchStockData = async () => {
      try {
        const response = await fetch(`${API}/base_de_donne_stock`);
        const data = await response.json();
        setStockData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Erreur stock:', error);
      }
    };
    fetchStockData();
  }, []);

  /* ---------- Filtres ---------- */
  const resetFilters = () => {
    setItemFilter('');
    setNumeroCommandeFilter('');
    setFournisseurFilter('');
  };

  const nbFiltres = [itemFilter, numeroCommandeFilter, fournisseurFilter].filter(Boolean).length;

  // Commandes : triées par date de livraison, puis filtrées (Item + N° commande + Fournisseur)
  const commandesTriees = useMemo(
    () => [...commandeFournisseurData].sort((a, b) => new Date(a['Date de livraison']) - new Date(b['Date de livraison'])),
    [commandeFournisseurData]
  );

  const commandesFiltrees = commandesTriees.filter(item =>
    contient(item['Item'], itemFilter) &&
    contient(item['Numero de commande'], numeroCommandeFilter) &&
    contient(item['Fournisseur'], fournisseurFilter)
  );

  // Stock : filtré par Item uniquement
  const stockFiltre = stockData.filter(item => contient(item['Item'], itemFilter));

  const colonnesCommandes = Object.keys(commandeFournisseurData[0] || {}).filter(c => !MASQUEES_COMMANDES.includes(c));
  const colonnesEmplacement = Object.keys(emplacementVideData[0] || {}).filter(c => !MASQUEES_EMPLACEMENT.includes(c));

  /* ===================================================
     RENDU D'UN TABLEAU
     =================================================== */
  const renderCellule = (item, column) => {
    const value = item[column];

    if (column === 'Horizon') {
      return <td key={column}><span className={`rp-badge rp-badge-${toneHorizon(value)}`}>{value}</span></td>;
    }
    if (column === 'Date de livraison' && value) {
      return <td key={column} className="rp-mono">{formatDate(value)}</td>;
    }
    if (column === 'Item') {
      return <td key={column} className="rp-mono rp-strong">{value}</td>;
    }
    if (['Numero de commande', 'Numero de ligne', 'Emplacement'].includes(column)) {
      return <td key={column} className="rp-mono">{value}</td>;
    }
    if (estNombre(value)) {
      return <td key={column} className="num rp-mono">{Number(String(value).replace(',', '.')).toLocaleString('fr-FR')}</td>;
    }
    return <td key={column}>{value}</td>;
  };

  const renderTable = (data, columns, { rowTone = false } = {}) => {
    if (data.length === 0) {
      return <div className="rp-empty">Item pas en stock / pas en commande</div>;
    }
    return (
      <table className="rp-table">
        <thead>
          <tr>
            {columns.map(column => (
              <th key={column} className={estNombre(data[0]?.[column]) ? 'num' : ''}>
                {LIBELLES[column] || column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index} className={rowTone && item['Horizon'] ? `rp-row-${toneHorizon(item['Horizon'])}` : ''}>
              {columns.map(column => renderCellule(item, column))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const IconeRecherche = () => (
    <svg className="rp-filter-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
    </svg>
  );

  /* ===================================================
     RENDU
     =================================================== */
  return (
    <div className="rp-page">
      {/* ★ En-têtes et données centrés dans les 3 tableaux (prioritaire sur Reception.css,
            y compris la classe "num" qui aligne les nombres à droite) */}
      <style>{`
        .rp-table th, .rp-table th.num,
        .rp-table td, .rp-table td.num { text-align: center !important; vertical-align: middle; }
      `}</style>
      <div className="rp-container">

        {/* ── Header ── */}
        <div className="rp-header">
          <div className="rp-title-row">
            <div className="rp-accent" />
            <h1 className="rp-title">Réception</h1>
            <span className="rp-chip-dept">LOG</span>
          </div>
         
        </div>

        {/* ── Graphique + emplacements vides + stock ── */}
        <div className="rp-top">
          <div className="rp-card rp-graph-card">
            <Graph3 width={GRAPH_LARGEUR} height={GRAPH_HAUTEUR} />
          </div>

          <div className="rp-card rp-side-card">
            <div className="rp-card-head">
              <div className="rp-card-title">Emplacements vides</div>
              <span className="rp-count">{emplacementVideData.length}</span>
            </div>
            <div className="rp-side-scroll">
              {renderTable(emplacementVideData, colonnesEmplacement)}
            </div>
          </div>

          <div className="rp-card rp-side-card">
            <div className="rp-card-head">
              <div className="rp-card-title">Stock</div>
              <span className="rp-count">{stockFiltre.length}{itemFilter ? ` / ${stockData.length}` : ''}</span>
              {itemFilter && <span className="rp-hint">filtré sur « {itemFilter} »</span>}
            </div>
            <div className="rp-side-scroll">
              {renderTable(stockFiltre, COLONNES_STOCK)}
            </div>
          </div>
        </div>

        {/* ── Filtres ── */}
        <div className="rp-card rp-filters">
          <div className="rp-filter">
            <IconeRecherche />
            <input
              type="text"
              className={itemFilter ? 'active' : ''}
              placeholder="Filtrer par Item…"
              aria-label="Filtrer par Item"
              value={itemFilter}
              onChange={(e) => setItemFilter(e.target.value)}
            />
          </div>
          <div className="rp-filter">
            <IconeRecherche />
            <input
              type="text"
              className={numeroCommandeFilter ? 'active' : ''}
              placeholder="Filtrer par N° de commande…"
              aria-label="Filtrer par numéro de commande"
              value={numeroCommandeFilter}
              onChange={(e) => setNumeroCommandeFilter(e.target.value)}
            />
          </div>
          <div className="rp-filter">
            <IconeRecherche />
            <input
              type="text"
              className={fournisseurFilter ? 'active' : ''}
              placeholder="Filtrer par Fournisseur…"
              aria-label="Filtrer par fournisseur"
              value={fournisseurFilter}
              onChange={(e) => setFournisseurFilter(e.target.value)}
            />
          </div>
          <button type="button" className="rp-btn rp-btn-ghost" onClick={resetFilters} disabled={nbFiltres === 0}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
            Réinitialiser les filtres{nbFiltres ? ` (${nbFiltres})` : ''}
          </button>
        </div>

        {/* ── Grand tableau des commandes fournisseurs ── */}
        <div className="rp-card rp-table-card">
          <div className="rp-card-head">
            <div className="rp-card-title">Commandes fournisseurs à réceptionner</div>
            <span className="rp-count">
              {commandesFiltrees.length}{nbFiltres ? ` / ${commandesTriees.length}` : ''} ligne{commandesFiltrees.length > 1 ? 's' : ''}
            </span>
            <div className="rp-legend">
              <span><i className="rp-dot rp-dot-danger" />Retard</span>
              <span><i className="rp-dot rp-dot-warn" />J</span>
              <span><i className="rp-dot rp-dot-ok" />À venir</span>
            </div>
          </div>
          <div className="rp-table-scroll">
            {renderTable(commandesFiltrees, colonnesCommandes, { rowTone: true })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Reception;